import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ticketAPI, userAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

const TicketDetail = () => {
  const { id } = useParams();
  const [ticket, setTicket] = useState(null);
  const [logs, setLogs] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [loading, setLoading] = useState(true);
  const [comment, setComment] = useState('');
  const [selectedTech, setSelectedTech] = useState('');
  const [seniors, setSeniors] = useState([]);
  const [selectedSenior, setSelectedSenior] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [showResolveModal, setShowResolveModal] = useState(false);
  const [resolveNote, setResolveNote] = useState('');
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [closeNote, setCloseNote] = useState('');
  const { tenant, user } = useAuth();
  const navigate = useNavigate();
  const primaryColor = tenant ? tenant.branding.primary_color || '#1B6B3A' : '#1B6B3A';
  const canDelete = user && (user.role === 'superadmin' || user.role === 'isp_admin');
  const canAssign = user && ['superadmin', 'isp_admin', 'dist_head', 'subdist_admin', 'area_engineer'].includes(user.role);
  const canClose = user && ['superadmin', 'isp_admin', 'dist_head', 'subdist_admin', 'subdist_tech_own', 'subdist_tech_isp', 'isp_technician', 'area_engineer', 'dist_l1', 'dist_l2', 'dist_l3'].includes(user.role);
  const isAssignedTech = ticket && user && ticket.assigned_to && (
    (ticket.assigned_to._id && (ticket.assigned_to._id === user._id || ticket.assigned_to._id === user.id)) ||
    (ticket.assigned_to === user._id || ticket.assigned_to === user.id) ||
    (ticket.assigned_to._id?.toString() === user._id?.toString())
  );
  const canParkRole = user && ['subdist_tech_own','subdist_tech_isp','isp_technician','dist_l1','dist_l2','dist_l3'].includes(user.role);
  const canPark = (isAssignedTech || canParkRole) && ticket && !['resolved','closed'].includes(ticket.status);
  const canEscalateTicket = user && ['superadmin', 'isp_admin', 'dist_head', 'subdist_admin', 'subdist_tech_own', 'subdist_tech_isp', 'isp_technician', 'isp_dist_head', 'area_engineer', 'dist_l1', 'dist_l2', 'dist_l3'].includes(user.role);
  const isDistL = user && ['dist_l1', 'dist_l2', 'dist_l3'].includes(user.role);
  const isViewer = user && ['director', 'isp_dist_head'].includes(user.role);
  const canViewProof = user && (user.role === 'superadmin' || user.role === 'isp_admin' || user.role === 'dist_head' || user.role === 'director');

  const loadTicket = () => {
    ticketAPI.getOne(id).then(res => {
      setTicket(res.data.ticket);
      setLogs(res.data.activity_log);
    }).finally(() => setLoading(false));
  };

  useEffect(() => {
    loadTicket();
    userAPI.getTechnicians().then(res => setTechnicians(res.data.technicians));
    if (user && ['dist_l1', 'dist_l2', 'dist_l3'].includes(user.role)) {
      userAPI.getSeniors().then(res => setSeniors(res.data.seniors || [])).catch(() => {});
    }
  }, [id]);

  const handleAssign = async () => {
    if (!selectedTech) return alert('Please select a technician');
    setActionLoading(true);
    try {
      await ticketAPI.assign(id, { assigned_to: selectedTech });
      loadTicket();
      alert('Ticket assigned successfully!');
    } catch (err) {
      alert('Failed to assign ticket');
    } finally { setActionLoading(false); }
  };

  const handleStatus = async (status, notes) => {
    setActionLoading(true);
    try {
      await ticketAPI.updateStatus(id, { status, notes });
      loadTicket();
      alert('Status updated successfully!');
    } catch (err) {
      alert('Failed to update status');
    } finally { setActionLoading(false); }
  };

  const handleEscalate = async () => {
    const reason = prompt('Enter escalation reason:');
    if (!reason) return;
    if (isDistL && !selectedSenior) return alert('Please select who to escalate to');
    setActionLoading(true);
    try {
      await ticketAPI.escalate(id, { reason, escalate_to: selectedSenior || null });
      loadTicket();
      alert('Ticket escalated successfully!');
    } catch (err) {
      alert('Failed to escalate ticket');
    } finally { setActionLoading(false); }
  };

  const parkReasons = ['Waiting for ONT Replacement','Waiting for Fiber Repair','Waiting for LCO Action','Waiting for Equipment','Others'];

  const handlePark = async () => {
    const reason = window.prompt('Select park reason:\n' + parkReasons.map((r,i)=>(i+1)+'. '+r).join('\n') + '\n\nEnter reason:');
    if (!reason) return;
    try {
      setActionLoading(true);
      await import('axios').then(({default:axios}) => {
        const token = localStorage.getItem('token');
        return axios.put(`/api/tickets/${id}/park`, { reason }, { headers: { Authorization: `Bearer ${token}` } });
      });
      await loadTicket();
    } catch(e) { alert('Error: ' + (e.response?.data?.message || e.message)); }
    finally { setActionLoading(false); }
  };

  const handleUnpark = async () => {
    if (!window.confirm('Unpark this ticket and resume work?')) return;
    try {
      setActionLoading(true);
      await import('axios').then(({default:axios}) => {
        const token = localStorage.getItem('token');
        return axios.put(`/api/tickets/${id}/unpark`, {}, { headers: { Authorization: `Bearer ${token}` } });
      });
      await loadTicket();
    } catch(e) { alert('Error: ' + (e.response?.data?.message || e.message)); }
    finally { setActionLoading(false); }
  };

  const handleComment = async () => {
    if (!comment.trim()) return;
    setActionLoading(true);
    try {
      await ticketAPI.addComment(id, { comment });
      setComment('');
      loadTicket();
    } catch (err) {
      alert('Failed to add comment');
    } finally { setActionLoading(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete ticket ' + (ticket ? ticket.ticket_number : '') + '?\n\nThis cannot be undone!')) return;
    try {
      await ticketAPI.delete(id);
      navigate('/tickets');
    } catch (err) {
      alert('Failed to delete ticket');
    }
  };

  const extractLocation = (text) => {
    if (!text) return null;
    const match = text.match(/https:\/\/maps\.google\.com\/\?q=([-\d.]+),([-\d.]+)/);
    if (match) return { url: match[0], lat: match[1], lng: match[2] };
    return null;
  };

  const extractPhotos = (text) => {
    if (!text) return [];
    const matches = text.match(/https:\/\/desk\.fibralink\.co\.in\/uploads\/photos\/[^\s\n"']*/g);
    return matches || [];
  };

  const renderLogDescription = (description) => {
    if (!description) return null;
    const location = extractLocation(description);
    const photos = extractPhotos(description);
    const cleanDesc = description
      .replace(/\n?📍 https:\/\/maps\.google\.com\/\?q=[^\s\n]*/g, '')
      .replace(/\n?📸 \d+ photo\(s\) attached/g, '')
      .replace(/\n?📸 Photos: \d+ attached/g, '')
      .trim();

    return (
      <div>
        {cleanDesc && <p style={{ margin: '4px 0 6px', fontSize: '13px', color: '#555', whiteSpace: 'pre-wrap' }}>{cleanDesc}</p>}
        {location && canViewProof && (
          <a href={location.url} target="_blank" rel="noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: '#e8f5e9', color: '#2e7d32', padding: '6px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: '600', textDecoration: 'none', marginBottom: '8px' }}>
            📍 View Location on Google Maps
          </a>
        )}
        {photos.length > 0 && canViewProof && (
          <div>
            <p style={{ margin: '6px 0 6px', fontSize: '12px', color: '#666', fontWeight: '600' }}>📸 Field Photos ({photos.length})</p>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {photos.map((url, i) => (
                <a key={i} href={url} target="_blank" rel="noreferrer">
                  <img src={url} alt={'Photo ' + (i+1)} style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '6px', border: '2px solid #e0e0e0', cursor: 'pointer' }} onError={e => e.target.style.display='none'} />
                </a>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const priorityColor = { high: '#f44336', critical: '#b71c1c', medium: '#ff9800', low: '#4caf50' };
  const statusColor = { open: '#2196f3', assigned: '#9c27b0', in_progress: '#ff9800', escalated: '#f44336', resolved: '#4caf50', closed: '#757575', reopened: '#e91e63' };
  const sourceTag = (source) => {
    if (source === 'public_url') return <span style={{ background: '#e3f2fd', color: '#1565c0', padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '600' }}>🌐 Online</span>;
    if (source === 'mobile_app') return <span style={{ background: '#e8f5e9', color: '#2e7d32', padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '600' }}>📱 App</span>;
    return <span style={{ background: '#f3e5f5', color: '#7b1fa2', padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '600' }}>💻 Staff</span>;
  };

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}><p>Loading...</p></div>;
  if (!ticket) return <div><p>Ticket not found</p></div>;

  // Close Modal
  const closeModal = showCloseModal && (
    <div style={{ position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.5)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:'20px' }}>
      <div style={{ background:'white', borderRadius:'16px', padding:'24px', maxWidth:'480px', width:'100%', boxShadow:'0 20px 60px rgba(0,0,0,0.3)' }}>
        <div style={{ fontSize:'18px', fontWeight:'800', color:'#1e293b', marginBottom:'4px' }}>🔒 Close Ticket</div>
        <div style={{ fontSize:'12px', color:'#64748b', marginBottom:'16px' }}>Describe what action was taken to close this ticket. Minimum 30 characters required.</div>
        <textarea
          value={closeNote}
          onChange={e=>setCloseNote(e.target.value)}
          placeholder="e.g. Customer confirmed issue resolved after ONT replacement. No further complaints. Ticket closed after 24hr follow-up."
          style={{ width:'100%', minHeight:'120px', border:`2px solid ${closeNote.length>=30?'#475569':closeNote.length>0?'#f59e0b':'#e2e8f0'}`, borderRadius:'8px', padding:'10px 12px', fontSize:'13px', outline:'none', resize:'vertical', boxSizing:'border-box', fontFamily:'inherit' }}
          autoFocus
        />
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:'8px', marginBottom:'16px' }}>
          <span style={{ fontSize:'11px', color:closeNote.length>=30?'#475569':closeNote.length>0?'#d97706':'#94a3b8', fontWeight:'600' }}>
            {closeNote.length}/30 min {closeNote.length>=30?'✅':'characters'}
          </span>
          <span style={{ fontSize:'11px', color:'#94a3b8' }}>{closeNote.length} characters</span>
        </div>
        <div style={{ display:'flex', gap:'10px' }}>
          <button onClick={()=>setShowCloseModal(false)} style={{ flex:1, padding:'10px', border:'1px solid #e2e8f0', borderRadius:'8px', background:'white', cursor:'pointer', fontSize:'13px', fontWeight:'600', color:'#64748b' }}>Cancel</button>
          <button
            onClick={()=>{ if(closeNote.trim().length<30){ alert('Please provide closure details (minimum 30 characters).'); return; } setShowCloseModal(false); handleStatus('closed', closeNote.trim()); }}
            disabled={closeNote.length<30}
            style={{ flex:2, padding:'10px', border:'none', borderRadius:'8px', background:closeNote.length>=30?'#475569':'#94a3b8', color:'white', cursor:closeNote.length>=30?'pointer':'not-allowed', fontSize:'13px', fontWeight:'700' }}>
            {closeNote.length>=30?'🔒 Close Ticket':'Enter details first...'}
          </button>
        </div>
      </div>
    </div>
  );

  // Resolve Modal
  const resolveModal = showResolveModal && (
    <div style={{ position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.5)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:'20px' }}>
      <div style={{ background:'white', borderRadius:'16px', padding:'24px', maxWidth:'480px', width:'100%', boxShadow:'0 20px 60px rgba(0,0,0,0.3)' }}>
        <div style={{ fontSize:'18px', fontWeight:'800', color:'#1e293b', marginBottom:'4px' }}>✅ Resolve Ticket</div>
        <div style={{ fontSize:'12px', color:'#64748b', marginBottom:'16px' }}>Describe exactly how the issue was fixed. Minimum 30 characters required.</div>
        <textarea
          value={resolveNote}
          onChange={e=>setResolveNote(e.target.value)}
          placeholder="e.g. Replaced damaged fiber splice at pole #23 near customer premises. Signal restored to -18dBm. Customer confirmed internet working."
          style={{ width:'100%', minHeight:'120px', border:`2px solid ${resolveNote.length>=30?'#15803d':resolveNote.length>0?'#f59e0b':'#e2e8f0'}`, borderRadius:'8px', padding:'10px 12px', fontSize:'13px', outline:'none', resize:'vertical', boxSizing:'border-box', fontFamily:'inherit' }}
          autoFocus
        />
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:'8px', marginBottom:'16px' }}>
          <span style={{ fontSize:'11px', color:resolveNote.length>=30?'#15803d':resolveNote.length>0?'#d97706':'#94a3b8', fontWeight:'600' }}>
            {resolveNote.length}/30 min {resolveNote.length>=30?'✅':'characters'}
          </span>
          <span style={{ fontSize:'11px', color:'#94a3b8' }}>{resolveNote.length} characters</span>
        </div>
        <div style={{ display:'flex', gap:'10px' }}>
          <button onClick={()=>setShowResolveModal(false)} style={{ flex:1, padding:'10px', border:'1px solid #e2e8f0', borderRadius:'8px', background:'white', cursor:'pointer', fontSize:'13px', fontWeight:'600', color:'#64748b' }}>Cancel</button>
          <button
            onClick={()=>{ if(resolveNote.trim().length<30){ alert('Please provide a detailed resolution note (minimum 30 characters).'); return; } setShowResolveModal(false); handleStatus('resolved', resolveNote.trim()); }}
            disabled={resolveNote.length<30}
            style={{ flex:2, padding:'10px', border:'none', borderRadius:'8px', background:resolveNote.length>=30?'#15803d':'#94a3b8', color:'white', cursor:resolveNote.length>=30?'pointer':'not-allowed', fontSize:'13px', fontWeight:'700' }}>
            {resolveNote.length>=30?'✅ Submit Resolution':'Enter details first...'}
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <>>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button onClick={() => navigate('/tickets')} style={{ background: '#f5f5f5', border: 'none', padding: '8px 16px', borderRadius: '6px', cursor: 'pointer', fontSize: '14px' }}>← Back</button>
          <h2 style={{ margin: '0', color: '#333' }}>Ticket: {ticket.ticket_number}</h2>
          {sourceTag(ticket.source)}
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <span style={{ background: priorityColor[ticket.complaint.priority] + '20', color: priorityColor[ticket.complaint.priority], padding: '6px 12px', borderRadius: '6px', fontWeight: '600' }}>{ticket.complaint.priority}</span>
          <span style={{ background: statusColor[ticket.status] + '20', color: statusColor[ticket.status], padding: '6px 12px', borderRadius: '6px', fontWeight: '600' }}>{ticket.status}</span>
          {canDelete && <button onClick={handleDelete} style={{ background: '#ffebee', color: '#c62828', border: '1px solid #f44336', padding: '6px 14px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' }}>🗑️ Delete</button>}
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
        <div style={{ background: 'white', borderRadius: '10px', padding: '20px', boxShadow: '0 2px 10px rgba(0,0,0,0.08)' }}>
          <h3 style={{ margin: '0 0 16px', color: primaryColor }}>Customer Details</h3>
          <p style={{ margin: '0 0 8px' }}><strong>Name:</strong> {ticket.customer.name}</p>
          <p style={{ margin: '0 0 8px' }}><strong>Phone:</strong> {ticket.customer.phone}</p>
          <p style={{ margin: '0 0 8px' }}><strong>Address:</strong> {ticket.customer.address}</p>
          {ticket.customer.connection_id && <p style={{ margin: '0 0 8px' }}><strong>Connection ID:</strong> {ticket.customer.connection_id}</p>}
          {ticket.lco_id && <p style={{ margin: '0' }}><strong>LCO:</strong> {ticket.lco_id.name}</p>}
        </div>
        <div style={{ background: 'white', borderRadius: '10px', padding: '20px', boxShadow: '0 2px 10px rgba(0,0,0,0.08)' }}>
          <h3 style={{ margin: '0 0 16px', color: primaryColor }}>Complaint Details</h3>
          <p style={{ margin: '0 0 8px' }}><strong>Category:</strong> {ticket.complaint.category}</p>
          <p style={{ margin: '0 0 8px' }}><strong>Description:</strong> {ticket.complaint.description}</p>
          <p style={{ margin: '0 0 8px' }}><strong>SLA Deadline:</strong> {new Date(ticket.sla.deadline).toLocaleString()}</p>
          <p style={{ margin: '0 0 8px' }}><strong>Escalation Level:</strong> {ticket.status === 'escalated' && ticket.escalation.current_level > 0 ? (
  <span style={{ background: '#f4433620', color: '#f44336', padding: '2px 8px', borderRadius: '4px', marginLeft: '8px', fontSize: '13px' }}>
    Level {ticket.escalation.current_level} — {ticket.escalation.current_level === 1 ? 'With Sub Admin' : ticket.escalation.current_level === 2 ? 'With Dist Head' : 'With ISP Team'}
  </span>) : <span style={{ color: '#4caf50' }}>Not Escalated</span>}</p>
{ticket.escalation.current_level > 0 && ticket.escalation.escalated_by && (
  <p style={{ margin: '0 0 8px' }}><strong>Escalated By:</strong> {ticket.escalation.escalated_by.name} ({ticket.escalation.escalated_by.role})</p>
)}
{ticket.escalation.current_level > 0 && ticket.escalation.escalated_at && (
  <p style={{ margin: '0 0 8px' }}><strong>Escalated At:</strong> {new Date(ticket.escalation.escalated_at).toLocaleString()}</p>
)}
{ticket.status === 'escalated' && ticket.escalation.current_level > 0 && (
  <p style={{ margin: '0 0 8px', padding: '8px', background: '#fff3e0', borderRadius: '6px', fontSize: '13px', color: '#e65100' }}>
    ⚡ <strong>Action Required:</strong> {ticket.escalation.current_level === 1 ? 'Sub Distribution Admin should handle this ticket' : ticket.escalation.current_level === 2 ? 'Distribution Head (PDIC) should handle this ticket' : 'ISP Team should handle this ticket'}
  </p>
)}
          <p style={{ margin: '0 0 8px' }}><strong>Source:</strong> {ticket.source === 'public_url' ? 'Online (Public URL)' : ticket.source === 'mobile_app' ? 'Mobile App' : 'Staff'}</p>
          {ticket.created_by && <p style={{ margin: '0' }}><strong>Created By:</strong> {ticket.created_by.name} ({ticket.created_by.role})</p>}
          {ticket.status === 'parked' && ticket.park && (
            <div style={{ background:'#e0f7fa', border:'1px solid #0891b2', borderRadius:'8px', padding:'12px', marginTop:'12px' }}>
              <p style={{ margin:'0 0 4px', fontWeight:'700', color:'#0891b2' }}>🅿️ Ticket Parked</p>
              <p style={{ margin:'0 0 4px', fontSize:'13px' }}><strong>Reason:</strong> {ticket.park.reason}</p>
              <p style={{ margin:'0', fontSize:'12px', color:'#64748b' }}><strong>Parked at:</strong> {ticket.park.parked_at ? new Date(ticket.park.parked_at).toLocaleString('en-IN') : '-'}</p>
            </div>
          )}
        </div>
      </div>

      <div style={{ background: 'white', borderRadius: '10px', padding: '20px', boxShadow: '0 2px 10px rgba(0,0,0,0.08)', marginBottom: '20px' }}>
        <h3 style={{ margin: '0 0 16px', color: primaryColor }}>Actions</h3>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '16px' }}>
          {canAssign && <select value={selectedTech} onChange={e => setSelectedTech(e.target.value)} style={{ padding: '8px 12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px', minWidth: '200px' }}><option value="">Select Technician</option>{technicians.map(t => <option key={t._id} value={t._id}>{t.name} ({t.role})</option>)}</select>}
          {canAssign && <button onClick={handleAssign} disabled={actionLoading} style={{ padding: '8px 16px', background: '#9c27b0', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>Assign</button>}
          {canClose && <button onClick={() => handleStatus('in_progress', 'Work started')} disabled={actionLoading} style={{ padding: '8px 16px', background: '#ff9800', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>In Progress</button>}
          {canClose && <button onClick={()=>{setResolveNote('');setShowResolveModal(true);}} disabled={actionLoading} style={{ padding:'8px 16px', background:'#15803d', color:'white', border:'none', borderRadius:'6px', cursor:'pointer', fontWeight:'600' }}>✅ Resolve</button>}
          {isDistL && seniors.length > 0 && <select value={selectedSenior} onChange={e => setSelectedSenior(e.target.value)} style={{ padding: '8px 12px', border: '1px solid #f44336', borderRadius: '6px', fontSize: '14px', minWidth: '180px' }}><option value=''>Escalate To...</option>{seniors.map(s => <option key={s._id} value={s._id}>{s.name} ({s.role})</option>)}</select>}
          {canEscalateTicket && <button onClick={handleEscalate} disabled={actionLoading} style={{ padding: '8px 16px', background: '#f44336', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>Escalate</button>}
          {canClose && <button onClick={()=>{setCloseNote('');setShowCloseModal(true);}} disabled={actionLoading} style={{ padding:'8px 16px', background:'#475569', color:'white', border:'none', borderRadius:'6px', cursor:'pointer', fontWeight:'600' }}>🔒 Close</button>}
          {canPark && ticket.status !== 'parked' && <button onClick={handlePark} disabled={actionLoading} style={{ padding: '8px 16px', background: '#0891b2', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>🅿️ Park</button>}
          {canPark && ticket.status === 'parked' && <button onClick={handleUnpark} disabled={actionLoading} style={{ padding: '8px 16px', background: '#15803d', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>▶️ Unpark</button>}
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <input type="text" placeholder="Add a comment or work note..." value={comment} onChange={e => setComment(e.target.value)} style={{ flex: 1, padding: '10px 12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px' }} />
          <button onClick={handleComment} disabled={actionLoading} style={{ padding: '10px 20px', background: primaryColor, color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>Add Note</button>
        </div>
      </div>

      <div style={{ background: 'white', borderRadius: '10px', padding: '20px', boxShadow: '0 2px 10px rgba(0,0,0,0.08)' }}>
        <h3 style={{ margin: '0 0 16px', color: primaryColor }}>Activity Log</h3>
        {logs.length === 0 ? <p style={{ color: '#666' }}>No activity yet</p> : (
          <div>
            {logs.map(function(log) {
              return (
                <div key={log._id} style={{ display: 'flex', gap: '12px', marginBottom: '16px', paddingBottom: '16px', borderBottom: '1px solid #f0f0f0' }}>
                  <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: primaryColor + '20', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <span style={{ fontSize: '16px' }}>👤</span>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                      <strong style={{ fontSize: '13px' }}>{log.performed_by ? log.performed_by.name : 'System'}</strong>
                      <span style={{ fontSize: '12px', color: '#666' }}>{new Date(log.createdAt).toLocaleString()}</span>
                    </div>
                    {renderLogDescription(log.description)}
                    <span style={{ fontSize: '11px', color: '#999', background: '#f5f5f5', padding: '2px 6px', borderRadius: '4px' }}>{log.action}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    {resolveModal}
    {closeModal}
    </>
  );
};

export default TicketDetail;
